import { Snackbar, SnackbarContent } from "@material-ui/core";
import { alpha, makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import * as React from "react";

import { layoutTokens, textStyles } from "@/designTokens";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  success: {
    border: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
  },
  error: {
    border: `2px solid ${theme.palette.error.main}`,
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light,
  },
  message: {
    display: "flex",
    alignItems: "center",
    fontSize: theme.typography.button.fontSize,
    fontWeight: theme.typography.button.fontWeight,
  },
  root: {
    borderRadius: "8px",
    padding: theme.spacing(1.5),
    boxShadow: theme.shadows[3],
  },
  neutral: {
    "&&": {
      minWidth: 0,
      flexGrow: 0,
      padding: `${layoutTokens.space[12]}px ${layoutTokens.space[16]}px`,
      borderRadius: layoutTokens.radius.md,
      backgroundColor: theme.palette.semantic["surface/inverse"],
      color: theme.palette.semantic["text/inverse"],
      boxShadow: `0 2px 8px ${alpha(theme.palette.text.primary, 0.06)}`,
    },
  },
  neutralMessage: { padding: 0, "& span": textStyles["SP/Body/Large"] },
  neutralAnchor: {
    "&.MuiSnackbar-anchorOriginBottomCenter": {
      bottom: `calc(${layoutTokens.space[40]}px + env(safe-area-inset-bottom, 0px))`,
      left: "50%",
      right: "auto",
      transform: "translateX(-50%)",
      maxWidth: `calc(100% - ${layoutTokens.space[48]}px)`,
    },
  },
}));

interface CustomToastProps {
  open: boolean;
  variant: string;
  message: string;
  onClose: () => void;
}

export const Toast: React.FC<CustomToastProps> = ({ open, variant, message, onClose }) => {
  const classes = useStyles();
  const neutral = variant === "neutral";

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={neutral ? { vertical: "bottom", horizontal: "center" } : undefined}
      className={neutral ? classes.neutralAnchor : undefined}
    >
      <SnackbarContent
        className={clsx(classes[variant], classes.root)}
        classes={neutral ? { message: classes.neutralMessage } : undefined}
        role={neutral ? "status" : "alert"}
        message={<span className={classes.message}>{message}</span>}
        onClick={onClose}
      />
    </Snackbar>
  );
};

// サンプルコンポーネントでトーストを使用する
export const ToastExample: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [variant, setVariant] = React.useState("success");
  const [message, setMessage] = React.useState("");

  const handleClick = (variant, message) => {
    setVariant(variant);
    setMessage(message);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <button onClick={() => handleClick("success", "設定を適用しました")}>Show Success Toast</button>
      <button onClick={() => handleClick("error", "設定を適用できませんでした")}>Show Error Toast</button>
      <Toast open={open} variant={variant} message={message} onClose={handleClose} />
    </div>
  );
};
